import { tx } from "@/i18n/tx";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, Check, Wand2 } from "lucide-react";

function genPassword() {
  const words = ["luna", "sole", "salvia", "ponte", "fiore", "acqua", "sentiero", "farfalla"];
  const w = () => words[Math.floor(Math.random() * words.length)];
  const n = Math.floor(10 + Math.random() * 89);
  return `${w()}-${w()}-${n}`;
}

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const m = params.get("mode");
    if (m === "login" || m === "register") setMode(m);
    // eslint-disable-next-line
  }, [params]);

  const routeAfter = (user) => {
    if (user.role === "admin") navigate("/admin");
    else if (!user.onboarding_completed) navigate("/onboarding");
    else navigate("/app");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register") {
      if (password.length < 8) { toast.error(t("auth.errPwMin")); return; }
      if (!terms) { toast.error(t("auth.errTerms")); return; }
    }
    setBusy(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email, password }
        : { email, password, name, terms_consent: terms, marketing_consent: marketing };
      const { data } = await api.post(url, payload);
      setUser(data.user);
      routeAfter(data.user);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const suggest = () => { const p = genPassword(); setPassword(p); setShowPw(true); toast.success(t("auth.pwSuggested")); };

  return (
    <div className="anima-backdrop min-h-screen w-full flex justify-center">
      <div data-testid="auth-container" className="w-full max-w-md mx-auto px-6 py-10 flex flex-col min-h-screen">
        <button data-testid="auth-back-btn" onClick={() => navigate("/")} className="self-start text-anima-soft mb-8 flex items-center gap-1 text-sm">
          <ArrowLeft size={16} /> {t("common.back")}
        </button>

        <p className="font-serif text-2xl text-anima-strong mb-6">{tx("Ascolta Anima")}</p>

        <h1 className="font-serif text-4xl text-anima-strong mb-2">
          {mode === "login" ? t("auth.loginTitle") : t("auth.registerTitle")}
        </h1>
        <p className="text-anima-soft mb-8">
          {mode === "login" ? t("auth.loginSubtitle") : t("auth.registerSubtitle")}
        </p>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="text-sm text-anima-soft ml-1">{t("auth.nameLabel")}</label>
              <input
                data-testid="auth-name-input"
                value={name} onChange={(e) => setName(e.target.value)} required
                className="w-full mt-1 rounded-2xl bg-white/60 border border-anima-ink/10 px-4 py-3.5 outline-none focus:border-anima-sage transition-colors"
                placeholder={t("auth.namePlaceholder")}
              />
            </div>
          )}
          <div>
            <label className="text-sm text-anima-soft ml-1">{t("auth.email")}</label>
            <input
              data-testid="auth-email-input" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full mt-1 rounded-2xl bg-white/60 border border-anima-ink/10 px-4 py-3.5 outline-none focus:border-anima-sage transition-colors"
              placeholder={t("auth.emailPlaceholder")}
            />
          </div>
          <div>
            <div className="flex items-center justify-between ml-1">
              <label className="text-sm text-anima-soft">{t("auth.password")}</label>
              {mode === "register" && (
                <button type="button" onClick={suggest} data-testid="auth-suggest-pw" className="text-xs text-anima-sagestrong flex items-center gap-1">
                  <Wand2 size={12} /> {t("auth.suggest")}
                </button>
              )}
            </div>
            <div className="relative mt-1">
              <input
                data-testid="auth-password-input" type={showPw ? "text" : "password"}
                value={password} onChange={(e) => setPassword(e.target.value)} required minLength={mode === "register" ? 8 : undefined}
                className="w-full rounded-2xl bg-white/60 border border-anima-ink/10 px-4 py-3.5 pr-12 outline-none focus:border-anima-sage transition-colors"
                placeholder={mode === "register" ? t("auth.pwRegister") : t("auth.pwLogin")}
              />
              <button type="button" data-testid="auth-toggle-password" onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-anima-soft p-1">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {mode === "login" && (
              <button type="button" data-testid="auth-forgot-link" onClick={() => navigate("/forgot-password")} className="mt-2 ml-1 text-sm text-anima-sagestrong">
                {t("auth.forgot")}
              </button>
            )}
          </div>

          {mode === "register" && (
            <div className="space-y-2.5 pt-1">
              <button type="button" data-testid="auth-terms-checkbox" onClick={() => setTerms((v) => !v)} className="w-full flex items-start gap-3 text-left">
                <span className={`w-5 h-5 rounded-md border flex items-center justify-center mt-0.5 shrink-0 ${terms ? "bg-anima-sagestrong border-anima-sagestrong" : "border-anima-ink/25"}`}>
                  {terms && <Check size={13} className="text-white" />}
                </span>
                <span className="text-sm text-anima-strong leading-snug">
                  {t("auth.terms1")}
                  <Link to="/terms" target="_blank" className="underline text-anima-sagestrong">{t("auth.termsLink")}</Link>
                  {t("auth.terms2")}
                  <Link to="/privacy" target="_blank" className="underline text-anima-sagestrong">{t("auth.privacyLink")}</Link>
                  {t("auth.terms3")}
                </span>
              </button>
              <button type="button" data-testid="auth-marketing-checkbox" onClick={() => setMarketing((v) => !v)} className="w-full flex items-start gap-3 text-left">
                <span className={`w-5 h-5 rounded-md border flex items-center justify-center mt-0.5 shrink-0 ${marketing ? "bg-anima-sage border-anima-sage" : "border-anima-ink/25"}`}>
                  {marketing && <Check size={13} className="text-white" />}
                </span>
                <span className="text-sm text-anima-soft leading-snug">
                  {t("auth.marketing")}
                </span>
              </button>
            </div>
          )}

          <button
            data-testid="auth-submit-btn" type="submit" disabled={busy}
            className="w-full rounded-full bg-anima-sagestrong text-anima-ivory py-4 font-medium active:scale-95 transition-transform duration-200 disabled:opacity-60"
          >
            {busy ? t("auth.busy") : mode === "login" ? t("auth.login") : t("auth.register")}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-anima-ink/10" />
          <span className="text-xs text-anima-soft">{t("auth.or")}</span>
          <div className="h-px flex-1 bg-anima-ink/10" />
        </div>

        <button
          data-testid="auth-google-btn" onClick={googleLogin}
          className="w-full rounded-full border border-anima-ink/15 bg-white/60 text-anima-strong py-3.5 font-medium active:scale-95 transition-transform duration-200 flex items-center justify-center gap-2"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
          {t("auth.google")}
        </button>

        <button
          data-testid="auth-toggle-mode"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-8 text-sm text-anima-soft text-center"
        >
          {mode === "login" ? t("auth.noAccount") : t("auth.hasAccount")}
          <span className="text-anima-sagestrong underline">{mode === "login" ? t("auth.registerCta") : t("auth.loginCta")}</span>
        </button>
      </div>
    </div>
  );
}
